import { UserExpiration, UserExpirationWithId } from '@queries/users';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { userSelector } from '@client-shared/store/selectors';
import { filterUsers } from '../../userSettings/utils';
import moment from 'moment';

const formatUsers = (users: UserExpiration[]) =>
  users.map((user, index) => ({ ...user, id: index }));

const isValidExpirationDate = (date: string): boolean => {
  if (!date) {
    return false;
  }
  const expirationMoment = moment(date);
  const tomorrow = moment().add(1, 'day').startOf('day');
  const oneYearFromNow = moment().add(1, 'year').endOf('day');
  return expirationMoment.isSameOrAfter(tomorrow) && expirationMoment.isSameOrBefore(oneYearFromNow);
};

export const useExpiration = (users: UserExpiration[]) => {
  const [localUsers, setLocalUsers] = useState<UserExpirationWithId[]>([]);
  const [editedUsers, setEditedUsers] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const userLogged = useSelector(userSelector);

  const usersToUpdate = localUsers
    .filter(user => editedUsers.includes(user.id) && selectedUsers.includes(user.id))
    .map(({ id, ...attrs }) => attrs);

  const revertEditedUsers = () => {
    setLocalUsers(formatUsers(users));
    setSelectedUsers([]);
    setEditedUsers([]);
  };

  const updateUsers = (expirationDate: string) => {
    if (!isValidExpirationDate(expirationDate)) {
      return;
    }
    const newUsersList = localUsers.map(user => selectedUsers.includes(user.id)
      ? { ...user, expirationDate } : user);
    setLocalUsers(newUsersList);

    const filteredEdited = newUsersList.filter(user => selectedUsers.includes(user.id)).map(({ id }) => id);
    setEditedUsers(Array.from(new Set([...editedUsers, ...filteredEdited])));
  };

  const clearSelectedUsers = () => {
    setSelectedUsers([]);
    setEditedUsers([]);
  };

  useEffect(() => {
    const filteredUsers = filterUsers(userLogged.email || '', users);
    setLocalUsers(formatUsers(filteredUsers));
  }, [users, userLogged]);

  return {
    localUsers,
    editedUsers,
    selectedUsers,
    usersToUpdate,
    setSelectedUsers,
    updateUsers,
    revertEditedUsers,
    clearSelectedUsers,
  };
};
